import type { JobReasonCodeInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Switch } from 'antd';
import { useEffect } from 'react';
import type { JobReasonCode } from '../../../reference-data.api';

interface ReasonCodeFormDrawerProps {
  open: boolean;
  /** Present to edit that row; absent to create a new one. */
  editing?: JobReasonCode;
  error: string | null;
  submitting: boolean;
  onSubmit: (input: JobReasonCodeInput) => Promise<boolean>;
  onClose: () => void;
}

/** One drawer for both create and edit — "edit every field" means there's no separate patch shape. */
export function ReasonCodeFormDrawer({ open, editing, error, submitting, onSubmit, onClose }: ReasonCodeFormDrawerProps) {
  const [form] = Form.useForm<JobReasonCodeInput>();

  useEffect(() => {
    if (open) form.setFieldsValue(editing ?? { code: '', name: '', appliesToHold: false, appliesToCancel: false });
  }, [open, editing, form]);

  async function submit(values: JobReasonCodeInput) {
    const saved = await onSubmit(values);
    if (saved) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit reason code' : 'New reason code'} placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<JobReasonCodeInput> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="appliesToHold" label="Applies to hold" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="appliesToCancel" label="Applies to cancel" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create reason code'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
